import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Save, Loader2, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["ga4_id", "Google Analytics 4 ID", "G-XXXXXXXXXX"],
  ["gtm_id", "Google Tag Manager ID", "GTM-XXXXXXX"],
  ["google_business_url", "Google Business profile URL", "https://g.page/..."],
  ["org_name", "Organization name", "Enhancing Your World"],
  ["org_logo", "Organization logo URL", "https://…/logo.png"],
  ["social_instagram", "Instagram URL", "https://instagram.com/…"],
  ["social_facebook", "Facebook URL", "https://facebook.com/…"],
  ["social_youtube", "YouTube URL", "https://youtube.com/@…"],
];

export default function SeoPanel() {
  const { data } = useQuery({ queryKey: ["admin-integrations"], queryFn: () => api.get("/admin/integrations").then((r) => r.data) });
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setF(FIELDS.reduce((a, [k]) => ({ ...a, [k]: data[k] || "" }), {})); }, [data]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/integrations", f); toast.success("SEO settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="max-w-2xl" data-testid="admin-seo-panel">
      <div className="flex items-center gap-2 mb-1"><Search size={20} className="text-[#144F75]" /><h2 className="font-serif text-2xl text-[#1A242B]">SEO &amp; Social</h2></div>
      <p className="text-sm text-[#4A5D6B] mb-6">Analytics (GA4/GTM with consent mode), schema markup, and social/Google Business links.</p>
      <div className="eyw-card p-6 space-y-4">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k}><label className="text-sm font-medium text-[#1A242B] mb-1 block">{label}</label>
            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} data-testid={`seo-${k}`} className="eyw-input w-full" /></div>
        ))}
        <div className="flex justify-end"><button onClick={save} disabled={saving} data-testid="seo-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} <Save size={14} /> Save</button></div>
      </div>
      <div className="eyw-card p-6 mt-6">
        <h3 className="font-medium text-[#1A242B] mb-2">Search assets</h3>
        <p className="text-sm text-[#4A5D6B] mb-3">Analytics loads only after the visitor accepts analytics cookies (Consent Mode v2). Organization &amp; WebSite JSON-LD schema is injected on every page.</p>
        <div className="flex flex-col gap-2 text-sm">
          <a href={`${origin}/api/sitemap.xml`} target="_blank" rel="noreferrer" data-testid="seo-sitemap-link" className="text-[#144F75] hover:underline flex items-center gap-1">Sitemap <ExternalLink size={13} /></a>
          <a href={`${origin}/api/robots.txt`} target="_blank" rel="noreferrer" data-testid="seo-robots-link" className="text-[#144F75] hover:underline flex items-center gap-1">robots.txt <ExternalLink size={13} /></a>
        </div>
        <p className="text-xs text-[#8FA89B] mt-3">Production: add a rewrite from <code>/sitemap.xml → /api/sitemap.xml</code> and <code>/robots.txt → /api/robots.txt</code>.</p>
      </div>
    </div>
  );
}
